import {AsyncResult, Loaded, Loading, ServerError} from 'Core/models/Async';
import {isRight} from 'fp-ts/lib/Either';
import {all, call, put, select, takeLatest} from 'redux-saga/effects';
import {ReducerState} from 'rootReducer';
import A360CommissionsAPI from '../api/a360CommissionsApi';
import {
  A360AdjustmentDetails,
  A360EarnedStatement,
  A360EmployerSummary,
  A360AdvancedStatement,
  CommissionSummary,
  CommissionSummaryGrouped,
  PaymentReceipt,
  Producer,
} from '../components/A360Commissions/types';
import {
  A360CommissionActionType,
  A360CommissionState,
  changeAdjustmentDetails,
  changeAvailableProducerCodes,
  changeCommissionPayable,
  changeCommissionSummary,
  changeEarnedStatements,
  changeEmployerSummary,
  changePaymentReceipts,
  getCommissionPayable,
  getEarnedStatements,
  getEmployerSummary,
  changeAvailableStatementDates,
  changeAvailableEmployerNames,
  updateSelectedProducerCodes,
  updateSelectedStatementDates,
  updateSelectedEmployerNames,
  changeIsInitialFilterLoading,
  getAdvancedStatements,
  changeAdvancedStatements,
} from '../ducks/a360Commissions';
import {replaceEmptyNamesWithNoEmployer, revertNoEmployerToEmpty} from '../components/A360Commissions/utilities';

const removeEmptyValues = (list: string[]): string[] => (Array.isArray(list) ? list.filter(e => e.trim() !== '') : []);

function* fetchInitialFilters(action) {
  try {
    yield put(changeIsInitialFilterLoading(true));
    const {isNewYorkSelected} = action.payload;
    const producerCodesResponse: AsyncResult<Producer[]> = yield A360CommissionsAPI.fetchProducerCodesFilterData(isNewYorkSelected);
    if (isRight(producerCodesResponse)) {
      // If producer codes data is available
      const producerCode = `${producerCodesResponse.right[0].agentNumber} - ${producerCodesResponse.right[0].agentName}`;

      const statementDatesResponse: AsyncResult<{
        statementDates: string[];
      }> = yield call(A360CommissionsAPI.fetchStatementDatesFilterData, [producerCode]);
      if (isRight(statementDatesResponse)) {
        // If statement dates data is available
        const statementDate = statementDatesResponse.right.statementDates[0];

        const employerNamesResponse: AsyncResult<{employerNames: string[]}> = yield call(
          A360CommissionsAPI.fetchEmployerNamesFilterData,
          [producerCode],
          [statementDate]
        );
        if (isRight(employerNamesResponse)) {
          // If employer names data is available
          const availableEmployerNames = replaceEmptyNamesWithNoEmployer(employerNamesResponse.right.employerNames);
          yield all([
            put(changeAvailableProducerCodes(new Loaded(producerCodesResponse.right))),
            put(changeAvailableStatementDates(new Loaded(statementDatesResponse.right.statementDates))),
            put(changeAvailableEmployerNames(new Loaded(availableEmployerNames))),
            put(updateSelectedProducerCodes([producerCode])),
            put(updateSelectedStatementDates([statementDate])),
            put(updateSelectedEmployerNames(availableEmployerNames)),
          ]);
        } else {
          throw new Error('error fetching employer names.');
        }
      } else {
        throw new Error('error fetching statement dates');
      }
    } else {
      throw new Error('error fetching producer codes.');
    }
  } catch (err) {
    console.error(err.message);
    yield put(changeAvailableProducerCodes(new ServerError(err.message)));
  } finally {
    yield put(changeIsInitialFilterLoading(false));
  }
}

function* producerCodesFilterDataSaga() {
  yield takeLatest(A360CommissionActionType.GET_AVAILABLE_PRODUCER_CODES, fetchInitialFilters);
}

function* fetchStatementDatesFilterData() {
  const {filter}: A360CommissionState = yield select((state: ReducerState) => state.a360Commissions);
  try {

    yield put(changeAvailableStatementDates(new Loading()));
    const statementDatesResponse: AsyncResult<{statementDates: string[]}> = yield call(
      A360CommissionsAPI.fetchStatementDatesFilterData,
      filter.selectedProducerCodes
    );
    if (isRight(statementDatesResponse)) {
      // If statement dates data is available
      const statementDate = statementDatesResponse.right.statementDates[0];
      yield put(changeAvailableEmployerNames(new Loading()));

      const employerNamesResponse: AsyncResult<{
        employerNames: string[];
      }> = yield call(A360CommissionsAPI.fetchEmployerNamesFilterData, filter.selectedProducerCodes, [statementDate]);
      if (isRight(employerNamesResponse)) {
        // If employer names data is available
        const availableEmployerNames = replaceEmptyNamesWithNoEmployer(employerNamesResponse.right.employerNames);
        
        yield all([
          put(changeAvailableStatementDates(new Loaded(statementDatesResponse.right.statementDates))),
          put(changeAvailableEmployerNames(new Loaded(availableEmployerNames))),
          put(updateSelectedStatementDates([statementDate])),
          put(updateSelectedEmployerNames(availableEmployerNames)),
        ]);
      }
    } else {
      throw new Error('error while fetching statement dates');
    }
  } catch (err) {
    console.error(err.message);
    yield put(changeAvailableStatementDates(new ServerError(err.message)));
  }
}

function* statementDatesFilterDataSaga() {
  yield takeLatest(A360CommissionActionType.GET_AVAILABLE_STATEMENT_DATES, fetchStatementDatesFilterData);
}

function* fetchEmployerNamesFilterData() {
  const {filter}: A360CommissionState = yield select((state: ReducerState) => state.a360Commissions);
  try {
    yield put(changeAvailableEmployerNames(new Loading()));
    const response: AsyncResult<{employerNames: string[]}> = yield call(
      A360CommissionsAPI.fetchEmployerNamesFilterData,
      filter.selectedProducerCodes,
      filter.selectedStatementDates
    );
    if (isRight(response)) {
      // If employer names data is available
      const availableEmployerNames = replaceEmptyNamesWithNoEmployer(response.right.employerNames);
      yield all([
        put(changeAvailableEmployerNames(new Loaded(availableEmployerNames))),
        put(updateSelectedEmployerNames(availableEmployerNames)),
      ]);
    } else {
      throw new Error('Error while fetching employer names');
    }
  } catch (err) {
    console.error(err.message);
    yield put(changeAvailableEmployerNames(new ServerError(err.message)));
  }
}

function* employerNamesFilterDataSaga() {
  yield takeLatest(A360CommissionActionType.GET_AVAILABLE_EMPLOYER_NAMES, fetchEmployerNamesFilterData);
}

function* fetchEmployerSummary(action: ReturnType<typeof getEmployerSummary>) {
  const {filter}: A360CommissionState = yield select((state: ReducerState) => state.a360Commissions);
  try {
    yield put(changeEmployerSummary(new Loading()));

    const limit = action.payload.pageSize;
    const offset = action.payload.pageSize * action.payload.page;

    const response: AsyncResult<{
      results: A360EmployerSummary[];
      totalRecordCount: number;
    }> = yield call(
      A360CommissionsAPI.fetchEmployerSummary,
      filter.selectedProducerCodes,
      filter.selectedStatementDates,
      revertNoEmployerToEmpty(filter.selectedEmployerNames),
      limit,
      offset,
      action.payload.sortFields
    );
    if (isRight(response)) {
      yield put(
        changeEmployerSummary(
          new Loaded({
            results: response.right.results,
            totalCount: response.right.totalRecordCount,
          })
        )
      );
    } else {
      throw new Error('Failed to fetch employer summary.');
    }
  } catch (err) {
    console.error(err.message);
    yield put(changeEmployerSummary(new ServerError(err.message)));
  }
}

function* employerSummarySaga() {
  yield takeLatest(A360CommissionActionType.GET_EMPLOYER_SUMMARY, fetchEmployerSummary);
}

function* fetchCommissionPayable(action: ReturnType<typeof getCommissionPayable>) {
  const {filter}: A360CommissionState = yield select((state: ReducerState) => state.a360Commissions);
  try {
    yield put(changeCommissionPayable(new Loading()));

    const response: AsyncResult<string> = yield A360CommissionsAPI.fetchCommissionPayable(
      filter.selectedProducerCodes,
      filter.selectedStatementDates,
      revertNoEmployerToEmpty(filter.selectedEmployerNames),
    );

    if (isRight(response)) {
      const commissionPayable = !isNaN(Number(response.right)) ? Number(response.right) : undefined;
      yield put(changeCommissionPayable(new Loaded(commissionPayable)));
    } else {
      throw new Error('Failed to fetch Commission Payable.');
    }
  } catch (err) {
    console.error(err.message);
    yield put(changeCommissionPayable(new ServerError(err.message)));
  }
}

function* commissionPayableSaga() {
  yield takeLatest(A360CommissionActionType.GET_COMMISSION_PAYABLE, fetchCommissionPayable);
}

function* fetchPaymentReceipts() {
  try {
    yield put(changePaymentReceipts(new Loading()));

    const {filter}: A360CommissionState = yield select((state: ReducerState) => state.a360Commissions);
    const response: AsyncResult<PaymentReceipt[]> = yield call(
      A360CommissionsAPI.fetchPaymentReceipts,
      filter.selectedProducerCodes,
      filter.selectedStatementDates,
      revertNoEmployerToEmpty(filter.selectedEmployerNames),
    );

    if (isRight(response)) {
      yield put(changePaymentReceipts(new Loaded(response.right)));
    } else {
      yield put(changePaymentReceipts(new ServerError('Failed to fetch Payment Receipts.')));
    }
  } catch (err) {
    console.error(err);
  }
}

function* paymentReceiptsSaga() {
  yield takeLatest(A360CommissionActionType.GET_PAYMENT_RECEIPTS, fetchPaymentReceipts);
}

function* fetchAdjustmentDetails() {
  try {
    yield put(changeAdjustmentDetails(new Loading()));

    const {filter}: A360CommissionState = yield select((state: ReducerState) => state.a360Commissions);
    const response: AsyncResult<A360AdjustmentDetails[]> = yield call(
      A360CommissionsAPI.fetchAdjustmentDetails,
      filter.selectedProducerCodes,
      filter.selectedStatementDates,
      revertNoEmployerToEmpty(filter.selectedEmployerNames),
    );

    if (isRight(response)) {
      yield put(changeAdjustmentDetails(new Loaded(response.right)));
    } else {
      throw new Error('Failed to fetch Adjustment Details.')
    }
  } catch (err) {
    console.error(err);
    yield put(changeAdjustmentDetails(new ServerError(err.message)));
  }
}

function* fetchAdjustmentDetailsSaga() {
  yield takeLatest(A360CommissionActionType.GET_ADJUSTMENT_DETAILS, fetchAdjustmentDetails);
}

function* fetchCommissionSummary() {
  try {
    yield put(changeCommissionSummary(new Loading()));

    const {filter}: A360CommissionState = yield select((state: ReducerState) => state.a360Commissions);
    const response: AsyncResult<CommissionSummary> = yield call(
      A360CommissionsAPI.fetchCommissionSummary,
      filter.selectedProducerCodes,
      filter.selectedStatementDates,
      revertNoEmployerToEmpty(filter.selectedEmployerNames),
    );

    if (isRight(response)) {
      const commissionSummary = Array.isArray(response.right)
        ? response?.right?.reduce((acc, current) => {
            const key = current['reportingItemType'];
            if (!acc[key]) {
              acc[key] = [];
            }
            acc[key].push(current);
            return acc;
          }, {} as CommissionSummaryGrouped)
        : ({} as CommissionSummaryGrouped);

      yield put(changeCommissionSummary(new Loaded(commissionSummary)));
    } else {
      throw new Error('Failed to fetch Commission Summary.')
    }
  } catch (err) {
    console.error(err);
    yield put(changeCommissionSummary(new ServerError(err.message)));
  }
}

function* commissionSummarySaga() {
  yield takeLatest(A360CommissionActionType.GET_PAYMENT_RECEIPTS, fetchCommissionSummary);
}

function* fetchEarnedStatements(action: ReturnType<typeof getEarnedStatements>) {
  const {filter}: A360CommissionState = yield select((state: ReducerState) => state.a360Commissions);
  try {
    yield put(changeEarnedStatements(new Loading()));

    const limit = action.payload.pageSize;
    const offset = action.payload.pageSize * action.payload.page;
    const response: AsyncResult<{
      results: A360EarnedStatement[];
      totalRecordCount: number;
    }> = yield call(
      A360CommissionsAPI.fetchEarnedStatements,
      filter.selectedProducerCodes,
      filter.selectedStatementDates,
      revertNoEmployerToEmpty(filter.selectedEmployerNames),
      limit,
      offset,
      action.payload.sortFields
    );
    if (isRight(response)) {
      yield put(
        changeEarnedStatements(
          new Loaded({
            results: response.right.results,
            totalCount: response.right.totalRecordCount,
          })
        )
      );
    } else {
      throw new Error('error fetching producer codes');
    }
  } catch (err) {
    console.error(err.message);
    yield put(changeEarnedStatements(new ServerError(err.message)));
  }
}

function* earnedStatementsSaga() {
  yield takeLatest(A360CommissionActionType.GET_EARNED_STATEMENTS, fetchEarnedStatements);
}

function* fetchAdvancedStatements(action: ReturnType<typeof getAdvancedStatements>) {
  const {filter}: A360CommissionState = yield select((state: ReducerState) => state.a360Commissions);
  try {
    yield put(changeAdvancedStatements(new Loading()));

    const limit = action.payload.pageSize;
    const offset = action.payload.pageSize * action.payload.page;
    const response: AsyncResult<{
      results: A360AdvancedStatement[];
      totalRecordCount: number;
    }> = yield call(
      A360CommissionsAPI.fetchAdvancedStatements,
      filter.selectedProducerCodes,
      filter.selectedStatementDates,
      revertNoEmployerToEmpty(filter.selectedEmployerNames),
      limit,
      offset,
      action.payload.sortFields
    );
    if (isRight(response)) {
      yield put(
        changeAdvancedStatements(
          new Loaded({
            results: response.right.results,
            totalCount: response.right.totalRecordCount,
          })
        )
      );
    } else {
      throw new Error('error fetching advanced statement');
    }
  } catch (err) {
    console.error(err.message);
    yield put(changeAdvancedStatements(new ServerError(err.message)));
  }
}

function* advancedStatementsSaga() {
  yield takeLatest(A360CommissionActionType.GET_ADVANCED_STATEMENTS, fetchAdvancedStatements);
}

export default function*() {
  yield all([
    commissionPayableSaga(),
    commissionSummarySaga(),
    earnedStatementsSaga(),
    advancedStatementsSaga(),
    employerSummarySaga(),
    paymentReceiptsSaga(),
    statementDatesFilterDataSaga(),
    employerNamesFilterDataSaga(),
    producerCodesFilterDataSaga(),
    fetchAdjustmentDetailsSaga(),
  ]);
}
